import { useWindowSize } from '@uidotdev/usehooks'
import { FC, ReactNode } from 'react'
import {
  createGlobalStyle,
  css,
  keyframes,
  ThemeProvider,
} from 'styled-components'
import { getRgbaValue } from '../utils/styling'

interface ColorPalette {
  [key: string]: string
}

interface AlphaPalette {
  [key: string]: string
}

interface FunctionColors {
  success: string
  error: string
}

interface Units {
  [key: number]: number
}

interface TypographySizes {
  compact: number
  medium: number
  expanded: number
  expandedPlus: number
}

interface TypographyLineHeights {
  compact: number
  medium: number
  expanded: number
  expandedPlus: number
}

interface Typography {
  size: TypographySizes
  lineHeight: TypographyLineHeights
}

interface TextStyles {
  size: TypographySizes
  lineHeight: TypographyLineHeights
}

interface FontWeight {
  regular: number
  semibold: number
  black: string
  bold: number
  bolder: number
}

interface FontFamily {
  body: string
  header: string
}

interface HeadingStyles {
  '5XL': Typography
  '4XL': Typography
  '3XL': Typography
  '2XL': Typography
  XL: Typography
  L: Typography
  M: Typography
  S: Typography
  XS: Typography
}

interface TextStylesMap {
  '5XL': TextStyles
  '4XL': TextStyles
  '3XL': TextStyles
  '2XL': TextStyles
  XL: TextStyles
  L: TextStyles
  M: TextStyles
  S: TextStyles
  XS: TextStyles
  XXS: TextStyles
}

interface TypographyConfig {
  weight: FontWeight
  family: FontFamily
  heading: HeadingStyles
  text: TextStylesMap
}

interface IndexGrids {
  minWidth: number
  maxWidth: number
  margins: number
  gutters: number
}

interface Spacing {
  sectionPadding: number
  paddingL: number
  paddingM: number
  paddingS: number
}

interface Components {
  nav: string
}

interface LayoutMode {
  indexGrids: IndexGrids
  spacing: Spacing
  components: Components
}

interface LayoutConfig {
  compact: LayoutMode
  medium: LayoutMode
  expanded: LayoutMode
  expandedPlus: LayoutMode
}

interface SemanticSurface {
  primary: string
  minimalPrimary: string
  minimalSecondary: string
  minimalInvert: string
  brand: string
}

interface SemanticText {
  primary: string
  secondary: string
  invert: string
  brandPrimary: string
  brandSecondary: string
  danger: string
  error: string
}

interface SemanticStroke {
  primary: string
  secondary: string
  tertiary: string
  brand: string
  invert: string
}

interface SemanticIcon {
  primary: string
  brand: string
}

interface SemanticMode {
  surface: SemanticSurface
  text: SemanticText
  stroke: SemanticStroke
  icon: SemanticIcon
  states: {
    minimalAlpha: string
    minimalAlphaPrimary: string
    invertAlpha: string
  }
  function: {
    success: string
    error: string
  }
}

interface SemanticsConfig {
  zap: SemanticMode
}

export type CoreColors = 'neutral' | 'blueNeutral' | 'yellow' | 'coral' | 'cyan'

interface ThemeConfig {
  colors: {
    neutral: ColorPalette
    blueNeutral: ColorPalette
    yellow: ColorPalette
    coral: ColorPalette
    cyan: ColorPalette
    alpha: {
      neutral: AlphaPalette
    }
    function: FunctionColors
    brandPrimary: string
  }
  units: Units
  typography: TypographyConfig
  layout: LayoutConfig
  semantics: SemanticsConfig
  breakpoints: {
    compact: number
    medium: number
    expanded: number
    expandedPlus: number
  }
}

export const COMPACT_PAGE_MAX_WIDTH = 380
export const STANDARD_PAGE_MAX_WIDTH = 840
export const MYZAP_PAGE_MAX_WIDTH = 1140
export const FULL_SCREEN_PAGE_MAX_WIDTH = 2520

export const theme: ThemeConfig = {
  colors: {
    neutral: {
      0: '#ffffff',
      100: '#cacaca',
      200: '#b1b1b1',
      300: '#989898',
      400: '#7e7e7e',
      500: '#656565',
      600: '#4c4c4c',
      700: '#333333',
      800: '#191919',
      900: '#080808',
    },
    blueNeutral: {
      0: '#ffffff',
      100: '#cdcfd1',
      200: '#b4b8bb',
      300: '#9ba0a4',
      400: '#687076',
      500: '#4f585f',
      600: '#364149',
      700: '#1d2932',
      800: '#04111b',
      900: '#02080d',
    },
    brandPrimary: '#FFFF00',
    yellow: {
      100: '#ffffcf',
      200: '#fefe9f',
      300: '#fefe6f',
      400: '#fdfd3f',
      500: '#ffff00',
      600: '#caca0c',
      700: '#989809',
      800: '#656506',
      900: '#333303',
    },
    coral: {
      100: '#ffddd9',
      200: '#ffbcb3',
      300: '#ff9a8c',
      400: '#ff7966',
      500: '#ff5740',
      600: '#cc4633',
      700: '#993426',
      800: '#66231a',
      900: '#33110d',
    },
    cyan: {
      100: '#f1fdf9',
      200: '#e4fbf3',
      300: '#d6faee',
      400: '#c9f8e8',
      500: '#bbf6e2',
      600: '#96c5b5',
      700: '#709488',
      800: '#4b625a',
      900: '#25312d',
    },
    alpha: {
      neutral: {
        '0-4': 'rgba(255, 255, 255, 0.04)',
        '0-6': 'rgba(255, 255, 255, 0.06)',
        '0-8': 'rgba(255, 255, 255, 0.08)',
        '0-16': 'rgba(255, 255, 255, 0.16)',
        '0-40': 'rgba(255, 255, 255, 0.40)',
        '900-4': 'rgba(0, 0, 0, 0.4)',
        '900-8': 'rgba(0, 0, 0, 0.8)',
        '900-16': 'rgba(0, 0, 0, 0.16)',
        '900-40': 'rgba(0, 0, 0, 0.40)',
      },
    },
    function: {
      success: '#39ff41',
      error: '#ff5252',
    },
  },
  units: {
    0: 0,
    1: 1,
    2: 2,
    4: 4,
    6: 6,
    7: 7,
    8: 8,
    10: 10,
    12: 12,
    16: 16,
    18: 18,
    20: 20,
    24: 24,
    28: 28,
    32: 32,
    36: 36,
    40: 40,
    44: 44,
    48: 48,
    52: 52,
    56: 56,
    60: 60,
    64: 64,
    68: 68,
    72: 72,
    76: 76,
    80: 80,
    84: 84,
    88: 88,
  },
  typography: {
    weight: {
      regular: 300,
      semibold: 600,
      black: 'Black',
      bold: 700,
      bolder: 900,
    },
    family: {
      body: 'Space Grotesk',
      header: 'proxima nova',
    },
    heading: {
      '5XL': {
        size: {
          compact: 56,
          medium: 72,
          expanded: 72,
          expandedPlus: 72,
        },
        lineHeight: {
          compact: 60,
          medium: 76,
          expanded: 76,
          expandedPlus: 76,
        },
      },
      '4XL': {
        size: {
          compact: 48,
          medium: 64,
          expanded: 64,
          expandedPlus: 64,
        },
        lineHeight: {
          compact: 48,
          medium: 64,
          expanded: 64,
          expandedPlus: 64,
        },
      },
      '3XL': {
        size: {
          compact: 40,
          medium: 48,
          expanded: 48,
          expandedPlus: 56,
        },
        lineHeight: {
          compact: 40,
          medium: 48,
          expanded: 48,
          expandedPlus: 56,
        },
      },
      '2XL': {
        size: {
          compact: 32,
          medium: 40,
          expanded: 40,
          expandedPlus: 48,
        },
        lineHeight: {
          compact: 32,
          medium: 40,
          expanded: 40,
          expandedPlus: 48,
        },
      },
      XL: {
        size: {
          compact: 24,
          medium: 32,
          expanded: 32,
          expandedPlus: 32,
        },
        lineHeight: {
          compact: 28,
          medium: 36,
          expanded: 36,
          expandedPlus: 36,
        },
      },
      L: {
        size: {
          compact: 20,
          medium: 24,
          expanded: 24,
          expandedPlus: 24,
        },
        lineHeight: {
          compact: 24,
          medium: 32,
          expanded: 32,
          expandedPlus: 32,
        },
      },
      M: {
        size: {
          compact: 16,
          medium: 20,
          expanded: 20,
          expandedPlus: 20,
        },
        lineHeight: {
          compact: 20,
          medium: 24,
          expanded: 24,
          expandedPlus: 24,
        },
      },
      S: {
        size: {
          compact: 12,
          medium: 16,
          expanded: 16,
          expandedPlus: 16,
        },
        lineHeight: {
          compact: 16,
          medium: 24,
          expanded: 24,
          expandedPlus: 24,
        },
      },
      XS: {
        size: {
          compact: 8,
          medium: 8,
          expanded: 8,
          expandedPlus: 8,
        },
        lineHeight: {
          compact: 12,
          medium: 12,
          expanded: 12,
          expandedPlus: 12,
        },
      },
    },
    text: {
      '5XL': {
        size: {
          compact: 56,
          medium: 72,
          expanded: 72,
          expandedPlus: 72,
        },
        lineHeight: {
          compact: 60,
          medium: 76,
          expanded: 76,
          expandedPlus: 76,
        },
      },
      '4XL': {
        size: {
          compact: 48,
          medium: 64,
          expanded: 64,
          expandedPlus: 64,
        },
        lineHeight: {
          compact: 48,
          medium: 64,
          expanded: 64,
          expandedPlus: 64,
        },
      },
      '3XL': {
        size: {
          compact: 40,
          medium: 48,
          expanded: 48,
          expandedPlus: 56,
        },
        lineHeight: {
          compact: 40,
          medium: 48,
          expanded: 48,
          expandedPlus: 56,
        },
      },
      '2XL': {
        size: {
          compact: 32,
          medium: 40,
          expanded: 40,
          expandedPlus: 48,
        },
        lineHeight: {
          compact: 32,
          medium: 40,
          expanded: 40,
          expandedPlus: 48,
        },
      },

      XL: {
        size: {
          compact: 20,
          medium: 20,
          expanded: 20,
          expandedPlus: 20,
        },
        lineHeight: {
          compact: 24,
          medium: 24,
          expanded: 24,
          expandedPlus: 24,
        },
      },
      L: {
        size: {
          compact: 16,
          medium: 16,
          expanded: 16,
          expandedPlus: 16,
        },
        lineHeight: {
          compact: 20,
          medium: 20,
          expanded: 20,
          expandedPlus: 20,
        },
      },
      M: {
        size: {
          compact: 14,
          medium: 14,
          expanded: 14,
          expandedPlus: 14,
        },
        lineHeight: {
          compact: 18,
          medium: 18,
          expanded: 18,
          expandedPlus: 18,
        },
      },
      S: {
        size: {
          compact: 12,
          medium: 12,
          expanded: 12,
          expandedPlus: 12,
        },
        lineHeight: {
          compact: 16,
          medium: 16,
          expanded: 16,
          expandedPlus: 16,
        },
      },
      XS: {
        size: {
          compact: 10,
          medium: 10,
          expanded: 10,
          expandedPlus: 10,
        },
        lineHeight: {
          compact: 14,
          medium: 14,
          expanded: 14,
          expandedPlus: 14,
        },
      },
      XXS: {
        size: {
          compact: 8,
          medium: 8,
          expanded: 8,
          expandedPlus: 8,
        },
        lineHeight: {
          compact: 12,
          medium: 12,
          expanded: 12,
          expandedPlus: 12,
        },
      },
    },
  },
  layout: {
    compact: {
      indexGrids: {
        minWidth: 320,
        maxWidth: 600,
        margins: 16,
        gutters: 12,
      },
      spacing: {
        sectionPadding: 48,
        paddingL: 16,
        paddingM: 12,
        paddingS: 8,
      },
      components: {
        nav: 'compact',
      },
    },
    medium: {
      indexGrids: {
        minWidth: 601,
        maxWidth: 840,
        margins: 24,
        gutters: 24,
      },
      spacing: {
        sectionPadding: 60,
        paddingL: 20,
        paddingM: 16,
        paddingS: 12,
      },
      components: {
        nav: 'compact',
      },
    },
    expanded: {
      indexGrids: {
        minWidth: 841,
        maxWidth: 1440,
        margins: 32,
        gutters: 32,
      },
      spacing: {
        sectionPadding: 60,
        paddingL: 20,
        paddingM: 16,
        paddingS: 12,
      },
      components: {
        nav: 'expanded',
      },
    },
    expandedPlus: {
      indexGrids: {
        minWidth: 1441,
        maxWidth: 1600,
        margins: 64,
        gutters: 32,
      },
      spacing: {
        sectionPadding: 60,
        paddingL: 20,
        paddingM: 16,
        paddingS: 12,
      },
      components: {
        nav: 'expanded',
      },
    },
  },
  semantics: {
    zap: {
      surface: {
        primary: '#080808',
        minimalPrimary: 'rgba(255, 255, 255, 0.06)',
        minimalSecondary: 'rgba(255, 255, 255, 0.04)',
        minimalInvert: 'rgba(0, 0, 0, 40)',
        brand: '#ffff00',
      },
      text: {
        primary: '#ffffff',
        secondary: '#b1b1b1',
        invert: '#080808',
        brandPrimary: '#ffff00',
        brandSecondary: '#989809',
        danger: '#ff5740',
        error: '#ff5252',
      },
      stroke: {
        primary: '#ffffff',
        secondary: '#333333',
        tertiary: '#252525',
        brand: '#ffff00',
        invert: '#080808',
      },
      icon: {
        primary: '#ffffff',
        brand: '#ffff00',
      },
      states: {
        minimalAlpha: '#ffffff',
        minimalAlphaPrimary: '#ffffff',
        invertAlpha: '#000000',
      },
      function: {
        success: '#39FF41',
        error: '#FF5252',
      },
    },
  },
  breakpoints: {
    compact: 600, // max-width
    medium: 840, // max-width
    expanded: 1440, // max-width
    expandedPlus: 1600, // max-width
  },
}

type ZapSemanticSurface = keyof typeof theme.semantics.zap.surface
type ZapSemanticText = keyof typeof theme.semantics.zap.text
type ZapSemanticStroke = keyof typeof theme.semantics.zap.stroke
type ZapSemanticIcon = keyof typeof theme.semantics.zap.icon
type ZapSemanticFunction = keyof typeof theme.semantics.zap.function

export type ZapSemanticColors =
  | ZapSemanticSurface
  | ZapSemanticText
  | ZapSemanticStroke
  | ZapSemanticIcon
  | ZapSemanticFunction

// a hyphen seperated string of the semantic key followed by the specific color key
export type BarryUIColorKey =
  | `text-${ZapSemanticText}`
  | `surface-${ZapSemanticSurface}`
  | `stroke-${ZapSemanticStroke}`
  | `icon-${ZapSemanticIcon}`
  | `function-${ZapSemanticFunction}`

export const shortformBarryUISpacingKeyToLongform = {
  section: 'sectionPadding',
  l: 'paddingL',
  m: 'paddingM',
  s: 'paddingS',
  2: theme.units[2],
  4: theme.units[4],
  7: theme.units[7], // James went GoHorse lol
  8: theme.units[8],
  10: theme.units[10], // and again - next time he does I'll tell him off
  12: theme.units[12],
  16: theme.units[16],
  20: theme.units[20],
  24: theme.units[24],
  28: theme.units[28],
  32: theme.units[32],
  36: theme.units[36],
  40: theme.units[40],
  44: theme.units[44],
  48: theme.units[48],
  52: theme.units[52],
  56: theme.units[56],
  60: theme.units[60],
  64: theme.units[64],
  68: theme.units[68],
  72: theme.units[72],
  76: theme.units[76],
  80: theme.units[80],
  84: theme.units[84],
  88: theme.units[88],
}

export type UnitsKey = keyof typeof theme.units
export type ShortformBarryUISpacingKey =
  keyof typeof shortformBarryUISpacingKeyToLongform
export type BarryUISpacingKey = UnitsKey | ShortformBarryUISpacingKey

export const loadingPlaceholderAnimation = (scale = 1) => css`
  :after {
    content: '';
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    animation-duration: ${scale * 3.7}s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    animation-name: placeholderAnimate;
    background: linear-gradient(
      to right,
      rgba(100, 100, 100, 0.3) 2%,
      rgba(100, 100, 100, 0.4) 18%,
      rgba(100, 100, 100, 0.3) 33%
    );

    transform-origin: 0 0;
  }
  @keyframes placeholderAnimate {
    0% {
      background-position: -650px 0;
    }
    100% {
      background-position: 650px 0;
    }
  }
`

export const defaultTransition = css`
  transition: all 0.15s ease-in-out;
`

export const backgroundAnimation = keyframes`

0% {
  background-position: 15% 50%;
}
50% {
  background-position: 85% 50%;
}
100% {
  background-position: 15% 50%;
}

`

type Props = {
  children: ReactNode
}

const Provider: FC<Props> = ({ children }) => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      {children}
    </ThemeProvider>
  )
}

const GlobalStyle = createGlobalStyle`
body {
  -webkit-font-smoothing: antialiased;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

strong{
  font-family:'Space Grotesk'
}

span.gradient-text {
  margin-left: 0px;
  padding-right: 16px;
  background: linear-gradient(260.97deg, #F4ED0C -0.97%, #3d68ff 96.36%);
  background-size: 200%;
  animation: ${backgroundAnimation} 4s linear infinite;
  -webkit-background-clip: text;
  background-clip:text;
  -webkit-text-fill-color: transparent;
  font-family: 'Space Grotesk';
  height: 16px;
  width: 100%;
}
span.gradient-text.thin{
  font-family: 'Space Grotesk' !important;
  margin-left: 0px;
  padding-right: 2px;
  height: 16px;
  width: 100%;
}

span.gradient-text.negative{
  font-family: 'Space Grotesk' !important;
  margin-left: 1px;
  padding-right: 2px;
  height: 16px;
  width: 100%;
}

span.gradient-text.thinner{
  font-family: 'Space Grotesk' !important;
  margin-left: 0px;
  padding-right: 1px;
  height: 16px;
  width: 100%;
}

.Toastify__toast {
  .Toastify__close-button > svg {
    height: 12px;
    width: 10px;
  }
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #081117;
  color: #FFF;
  font-feature-settings: 'dlig' on, 'ss01' on;
  font-family: "Space Grotesk";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
  letter-spacing: -0.56px;
}
.Toastify__toast--error {
  color: #FF5740;
}

// FOR DEMOS ONLY - with great power comes great responsibility
iframe#webpack-dev-server-client-overlay {
  display: none;
}

.Dialog-overlay {
  background: rgba(0, 0, 0, 0.8);
  display: grid;
  place-items: center;
  z-index: 999999;
}

.Popover {
  z-index: 888888;
}


body::-webkit-scrollbar {
    display: none;
}

*::-webkit-scrollbar {
    display: none;
}


body, * {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
}


.DropdownRootMenu {
  border: none;
  background: none;
}

.DropdownRootMenu[data-open],
.DropdownRootMenu:hover {
}

.DropdownMenu {
  display: flex;

  border-radius: var(--units-4, 4px);
  border: var(--units-1, 1px) solid var(--stroke-secondary, #333);
  background: linear-gradient(
      0deg,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 0%,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 100%
  ),
  var(--surface-primary, #080808);

  /* background-blur-80 */
  backdrop-filter: blur(40px);
  outline: 0;
  z-index: 10000000000;
}

.DropdownMenuItem {
  background: none;
  width: 100%;
  border: none;
  margin: 0;
  outline: 0;
}

.DropdownMenuItem:focus {
  background: var(--highlighted);
  color: white;
}

.DropdownMenuItem[data-nested][data-open]:not([data-focus-inside]) {
  background: var(--highlighted);
  color: white;
}

.DropdownMenuItem[data-focus-inside][data-open] {
  background: var(--active-unfocused);
}

${generateCSSVariables(theme)}
`

export const paddingRules = css`
  padding: ${({ theme }) => theme.layout.expandedPlus.spacing.paddingM}px;
  ${({ theme }) => {
    const { breakpoints } = theme
    const rules = []
    for (const [key, value] of Object.entries(breakpoints)) {
      const paddingAmount = theme.layout[key].spacing.paddingM
      rules.push(
        `@media screen and (max-width: ${value}px) { padding: ${paddingAmount}px; }`,
      )
    }
    return rules.slice().reverse().join('\n')
  }}
`

export default Provider

function generateCSSVariables(theme: ThemeConfig) {
  const cssVariables: string[] = []
  const mediaQueries: { [key: string]: string[] } = {
    compact: [],
    medium: [],
    expanded: [],
    expandedPlus: [],
  }

  // Colors
  for (const [colorName, colorValues] of Object.entries(theme.colors)) {
    if (typeof colorValues === 'object' && !Array.isArray(colorValues)) {
      for (const [shade, value] of Object.entries(colorValues)) {
        cssVariables.push(`--${colorName}-${shade}: ${value};`)
      }
    }
  }

  // Units
  for (const [unit, value] of Object.entries(theme.units)) {
    cssVariables.push(`--units-${unit}: ${value}px;`)
  }

  // Typography
  for (const [style, styleValues] of Object.entries(theme.typography)) {
    if (typeof styleValues === 'object' && !Array.isArray(styleValues)) {
      for (const [subStyle, subStyleValues] of Object.entries(styleValues)) {
        if (
          typeof subStyleValues === 'object' &&
          !Array.isArray(subStyleValues)
        ) {
          for (const [attribute, attributeValues] of Object.entries(
            subStyleValues,
          )) {
            if (
              typeof attributeValues === 'object' &&
              !Array.isArray(attributeValues)
            ) {
              for (const [breakpoint, value] of Object.entries(
                attributeValues,
              )) {
                mediaQueries[breakpoint].push(
                  `--${toTitleCase(
                    style,
                  )}-${subStyle}-${subStyle}-${upperCamelCaseToKebabCase(
                    attribute,
                  )}: ${value}px;`,
                )
              }
            } else {
              cssVariables.push(
                `--${toTitleCase(
                  style,
                )}-${subStyle}-${subStyle}-${upperCamelCaseToKebabCase(
                  attribute,
                )}: ${attributeValues};`,
              )
            }
          }
        }
      }
    }
  }

  function upperCamelCaseToKebabCase(str: string) {
    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase()
  }

  const ATTRIBUTES_TO_CAPITALISE = ['gutters', 'margins', 'spacing', 'padding']
  // Layout
  for (const [mode, modeValues] of Object.entries(theme.layout)) {
    if (typeof modeValues === 'object' && !Array.isArray(modeValues)) {
      for (const [layoutAttribute, layoutAttributeValues] of Object.entries(
        modeValues,
      )) {
        if (
          typeof layoutAttributeValues === 'object' &&
          !Array.isArray(layoutAttributeValues)
        ) {
          for (const [subAttribute, subAttributeValue] of Object.entries(
            layoutAttributeValues,
          )) {
            const isPaddingSubAttribute =
              subAttribute.split('padding').length > 1
            mediaQueries[mode].push(
              `--${ATTRIBUTES_TO_CAPITALISE.includes(layoutAttribute)
                ? toTitleCase(layoutAttribute)
                : layoutAttribute === 'indexGrids'
                  ? 'Index-Grids'
                  : layoutAttribute
              }-${isPaddingSubAttribute
                ? `Padding-${subAttribute.split('padding')[1].toUpperCase()}`
                : ATTRIBUTES_TO_CAPITALISE.includes(subAttribute)
                  ? toTitleCase(subAttribute)
                  : subAttribute
              }: ${subAttributeValue}px;`,
            )
          }
        }
      }
    }
  }

  // Semantics
  for (const [, semanticValues] of Object.entries(theme.semantics)) {
    if (typeof semanticValues === 'object' && !Array.isArray(semanticValues)) {
      for (const [semanticAttribute, semanticAttributeValues] of Object.entries(
        semanticValues,
      )) {
        if (
          typeof semanticAttributeValues === 'object' &&
          !Array.isArray(semanticAttributeValues)
        ) {
          for (const [subAttribute, subAttributeValue] of Object.entries(
            semanticAttributeValues,
          )) {
            cssVariables.push(
              `--${semanticAttribute}-${subAttribute}: ${subAttributeValue};`,
            )
          }
        }
      }
    }
  }

  const mediaQueryStrings = {
    compact: `@media (max-width: 600px) {\n:root{  ${mediaQueries.compact.join(
      '\n  ',
    )}\n}\n}`,
    medium: `@media (min-width: 601px) and (max-width: 840px) {\n:root{  ${mediaQueries.medium.join(
      '\n  ',
    )}\n}\n}`,
    expanded: `@media (min-width: 841px) and (max-width: 1440px) {\n:root{  ${mediaQueries.expanded.join(
      '\n  ',
    )}\n}\n}`,
    expandedPlus: `@media (min-width: 1441px) {\n:root{  ${mediaQueries.expandedPlus.join(
      '\n  ',
    )}\n
      }
    }`,
  }

  return `:root {
  --type-weight-Regular: 300;
  --type-weight-Semibold: 600;
  --type-weight-Black: Black;
  --type-weight-Bold: 700;
  --type-weight-Bolder: 900;

  \n  ${cssVariables.join('\n  ')}\n}\n\n${mediaQueryStrings.compact}\n\n${mediaQueryStrings.medium
    }\n\n${mediaQueryStrings.expanded}\n\n${mediaQueryStrings.expandedPlus}`
}

export function useShouldShowForThreshold(
  breakpoint: keyof ThemeConfig['breakpoints'],
) {
  const { width: windowWidth } = useWindowSize()
  const breakpointWidth = theme.breakpoints[breakpoint]
  return windowWidth > breakpointWidth
}

export type CustomPaddingProps = {
  pad?: BarryUISpacingKey
  padL?: BarryUISpacingKey
  padR?: BarryUISpacingKey
  padT?: BarryUISpacingKey
  padB?: BarryUISpacingKey
  padV?: BarryUISpacingKey // vertical
  padH?: BarryUISpacingKey // horizontal
}

export const customPadding = css<CustomPaddingProps>`
  ${({ pad, theme }) => {
    if (!pad) return ''
    const { breakpoints } = theme
    const rules = []
    const isUnits = typeof pad === 'number'
    if (isUnits) return `padding: ${theme.units[pad]}px;`
    const shortformToKey = shortformBarryUISpacingKeyToLongform[pad]
    rules.push(
      `padding: ${theme.layout.expandedPlus.spacing[shortformToKey]}px;`,
    )
    for (const [key, value] of Object.entries(breakpoints)) {
      const paddingAmount = theme.layout[key].spacing[shortformToKey]
      rules.push(
        `@media screen and (max-width: ${value}px) { padding: ${paddingAmount}px; }`,
      )
    }
    return rules.slice().reverse().join('\n')
  }}
  ${({ padL, theme }) => {
    if (!padL) return ''
    const { breakpoints } = theme
    const rules = []
    const isUnits = typeof padL === 'number'
    if (isUnits) return `padding-left: ${theme.units[padL]}px;`
    const shortformToKey = shortformBarryUISpacingKeyToLongform[padL]
    rules.push(
      `padding-left: ${theme.layout.expandedPlus.spacing[shortformToKey]}px;`,
    )
    for (const [key, value] of Object.entries(breakpoints)) {
      const paddingAmount = theme.layout[key].spacing[shortformToKey]

      rules.push(
        `@media screen and (max-width: ${value}px) { padding-left: ${paddingAmount}px; }`,
      )
    }
    return rules.slice().reverse().join('\n')
  }}
${({ padR, theme }) => {
    if (!padR) return ''
    const { breakpoints } = theme
    const rules = []
    const isUnits = typeof padR === 'number'
    if (isUnits) return `padding-right: ${theme.units[padR]}px;`
    const shortformToKey = shortformBarryUISpacingKeyToLongform[padR]
    rules.push(
      `padding-right: ${theme.layout.expandedPlus.spacing[shortformToKey]}px;`,
    )
    for (const [key, value] of Object.entries(breakpoints)) {
      const paddingAmount = theme.layout[key].spacing[shortformToKey]

      rules.push(
        `@media screen and (max-width: ${value}px) { padding-right: ${paddingAmount}px; }`,
      )
    }
    return rules.slice().reverse().join('\n')
  }}
${({ padT, theme }) => {
    if (!padT) return ''
    const { breakpoints } = theme
    const rules = []
    const isUnits = typeof padT === 'number'
    if (isUnits) return `padding-top: ${theme.units[padT]}px;`
    const shortformToKey = shortformBarryUISpacingKeyToLongform[padT]
    rules.push(
      `padding-top: ${theme.layout.expandedPlus.spacing[shortformToKey]}px;`,
    )
    for (const [key, value] of Object.entries(breakpoints)) {
      const paddingAmount = theme.layout[key].spacing[shortformToKey]

      rules.push(
        `@media screen and (max-width: ${value}px) { padding-top: ${paddingAmount}px; }`,
      )
    }
    return rules.slice().reverse().join('\n')
  }}
${({ padB, theme }) => {
    if (!padB) return ''
    const { breakpoints } = theme
    const rules = []
    const isUnits = typeof padB === 'number'
    if (isUnits) return `padding-bottom: ${theme.units[padB]}px;`
    const shortformToKey = shortformBarryUISpacingKeyToLongform[padB]
    rules.push(
      `padding-bottom: ${theme.layout.expandedPlus.spacing[shortformToKey]}px;`,
    )
    for (const [key, value] of Object.entries(breakpoints)) {
      const paddingAmount = theme.layout[key].spacing[shortformToKey]

      rules.push(
        `@media screen and (max-width: ${value}px) { padding-bottom: ${paddingAmount}px; }`,
      )
    }
    return rules.slice().reverse().join('\n')
  }}
  ${({ padV, theme }) => {
    if (!padV) return ''
    const { breakpoints } = theme
    const rules = []
    const isUnits = typeof padV === 'number'
    if (isUnits)
      return `padding-top: ${theme.units[padV]}px; padding-bottom: ${theme.units[padV]}px;`
    const shortformToKey = shortformBarryUISpacingKeyToLongform[padV]
    rules.push(
      `padding-top: ${theme.layout.expandedPlus.spacing[shortformToKey]}px;padding-bottom: ${theme.layout.expandedPlus.spacing[shortformToKey]}px;`,
    )
    for (const [key, value] of Object.entries(breakpoints)) {
      const paddingAmount = theme.layout[key].spacing[shortformToKey]

      rules.push(
        `@media screen and (max-width: ${value}px) { padding-top: ${paddingAmount}px; padding-bottom: ${paddingAmount}px; }`,
      )
    }
    return rules.slice().reverse().join('\n')
  }}
  ${({ padH, theme }) => {
    if (!padH) return ''
    const { breakpoints } = theme
    const rules = []
    const isUnits = typeof padH === 'number'
    if (isUnits)
      return `padding-left: ${theme.units[padH]}px; padding-right: ${theme.units[padH]}px;`
    const shortformToKey = shortformBarryUISpacingKeyToLongform[padH]
    rules.push(
      `padding-left: ${theme.layout.expandedPlus.spacing[shortformToKey]}px;padding-right: ${theme.layout.expandedPlus.spacing[shortformToKey]}px;`,
    )
    for (const [key, value] of Object.entries(breakpoints)) {
      const paddingAmount = theme.layout[key].spacing[shortformToKey]

      rules.push(
        `@media screen and (max-width: ${value}px) { padding-left: ${paddingAmount}px; padding-right: ${paddingAmount}px; }`,
      )
    }
    return rules.slice().reverse().join('\n')
  }}
`

export const withIndexGutters = css<{ indexGutters?: boolean; ig?: boolean }>`
  ${({ indexGutters, ig }) =>
    (indexGutters || ig) &&
    css`
      gap: var(--Index-Grids-Gutters, 24px);
    `}
`

export const withIndexGuttersPadding = css<{
  indexGuttersPadding?: boolean
  igpv?: boolean
  igph?: boolean
}>`
  ${({ indexGuttersPadding, igpv }) =>
    (indexGuttersPadding || igpv) &&
    css`
      padding-top: var(--Index-Grids-Gutters, 24px);
      padding-bottom: var(--Index-Grids-Gutters, 24px);
    `}
  ${({ indexGuttersPadding, igph }) =>
    (indexGuttersPadding || igph) &&
    css`
      padding-left: var(--Index-Grids-Gutters, 24px);
      padding-right: var(--Index-Grids-Gutters, 24px);
    `}
`

export const indexGutterMargin = css<{ igmv?: boolean; igmh?: boolean }>`
  ${({ igmv }) =>
    igmv &&
    css`
      margin-top: var(--Index-Grids-Gutters, 24px);
      margin-bottom: var(--Index-Grids-Gutters, 24px);
    `}

  ${({ igmh }) =>
    igmh &&
    css`
      margin-left: var(--Index-Grids-Gutters, 24px);
      margin-right: var(--Index-Grids-Gutters, 24px);
    `}

  @media screen and (min-width: ${({ theme }) =>
    theme.layout.expanded.indexGrids.minWidth}px) {
      ${({ igmv }) =>
    igmv &&
    css`
          margin-top: var(--Index-Grids-Gutters, 48px);
          margin-bottom: var(--Index-Grids-Gutters, 48px);
        `}

      ${({ igmh }) =>
    igmh &&
    css`
          margin-left: var(--Index-Grids-Gutters, 48px);
          margin-right: var(--Index-Grids-Gutters, 48px);
        `}
    }
`

export function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function(txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

// We have introduced 2 concepts for styling everything that has a surface (Cards, Chips, Buttons etc)
// The first of these is style. This can be thought of as the parent attribute
//
// These are:
// - Filled: The brand colour fills the component in a solid manner
// - Outlined: This is a card with a border
// - Text: This is basically the same as oulined but without a border
// - Alpha: Similar to outlined but a bit jazzier
//
// In addition to the styles, we also have states. These are:
// - Enabled (Standard state)
// - Hovered
// - Focused (This is when you select something or have it active in some other sense)
// - Disabled

export type SurfaceType = 'filled' | 'outlined' | 'text' | 'alpha'

// good to have these final two states for flexibility even though we can :hover and :active in the css etc
export type SurfaceState = 'enabled' | 'focused' | 'disabled' | 'hovered'

export type BarrySurfaceWithStateProps = {
  bst?: SurfaceType
  bss?: SurfaceState
  nh?: boolean // no hover state - this is useful for if we want to use the bg and border on a card or something
}

// need props here to dictate the style and state? we could have a shortform and a longform version along with
export const barrySurface = css<BarrySurfaceWithStateProps>`
  ${({ theme, bst, bss, nh }) => {
    let rules = css``
    let alphaColorKey = ''
    let alphaPercentage = 0

    const themeColors = theme.semantics.zap

    if (!bst) return
    if (!bss) bss = 'enabled'

    const coreRules = css`
      ${nh
        ? ''
        : css`
            :hover {
              cursor: pointer;
              background: ${getRgbaValue(
          themeColors.states.minimalAlpha,
          0.08,
        )};
            }
          `}
    `

    if (bst === 'filled') {
      alphaColorKey = bss === 'disabled' ? 'invertAlpha' : 'minimalAlpha'
      alphaPercentage =
        (bss === 'disabled'
          ? 40
          : bss === 'hovered'
            ? 8
            : bss === 'focused'
              ? 8
              : 0) / 100
      const filledDisabledState = css`
        pointer-events: none;
        :after {
          background: ${getRgbaValue(themeColors.states.invertAlpha, 0.4)};
        }
      `
      rules = css`
        position: relative;
        background: ${themeColors.surface.brand};
        color: var(--text-invert);
        img {
          filter: saturate(0) brightness(0);
        }
        :after {
          content: '';
          width: 100%;
          height: 100%;
          background: ${getRgbaValue(
            themeColors.states[alphaColorKey],
            alphaPercentage,
          )};
          border-radius: 48px;
          transition: background 0.3s ease-in-out;
          position: absolute;
        }
        ${nh
          ? ''
          : css`
              :hover {
                :after {
                  background: ${getRgbaValue(
                    themeColors.states.minimalAlpha,
                    0.08,
                  )};
                }
              }
            `}
        ${bss === 'disabled' && filledDisabledState}
        :disabled {
          ${filledDisabledState}
        }
      `
    }

    if (bst === 'outlined') {
      alphaColorKey = bss === 'disabled' ? 'invertAlpha' : 'minimalAlphaPrimary'
      alphaPercentage =
        (bss === 'disabled'
          ? 40
          : bss === 'hovered'
            ? 4
            : bss === 'focused'
              ? 16
              : 0) / 100
      const disabledState = css`
        pointer-events: none;
        h1,
        h2,
        h3,
        h4,
        h5,
        h6,
        p,
        span,
        strong,
        a,
        li,
        ui {
          opacity: 0.4;
        }
        :after {
          background: ${getRgbaValue(themeColors.states.invertAlpha, 0.4)};
        }
      `
      const borderColor =
        bss === 'disabled'
          ? themeColors.stroke.tertiary
          : bss === 'focused'
            ? themeColors.stroke.primary
            : themeColors.stroke.secondary

      rules = css`
        position: relative;
        background: transparent;
        overflow: hidden;
        border: 1px solid ${borderColor};
        :after {
          content: '';
          width: 100%;
          height: 100%;
          background: ${getRgbaValue(
        themeColors.states[alphaColorKey],
        alphaPercentage,
      )};
          transition: background 0.3s ease-in-out;
          position: absolute;
        }
        ${nh
          ? ''
          : css`
              :hover {
                :after {
                  background: ${getRgbaValue(
            themeColors.states.minimalAlpha,
            0.04,
          )};
                }
              }
            `}
        ${bss === 'disabled' && disabledState}
        :disabled {
          ${disabledState}
        }
      `
    }

    // any other dev who sees this, I am not finishing this, I've been given a huge smart contract deadline and please forgive me but I think button styles can be deferred, despite what management says.
    if (bst === 'text') {
      alphaColorKey = bss === 'disabled' ? 'invertAlpha' : 'minimalAlphaPrimary'
      alphaPercentage =
        (bss === 'disabled'
          ? 40
          : bss === 'hovered'
            ? 4
            : bss === 'focused'
              ? 16
              : 0) / 100
      const disabledState = css`
        pointer-events: none;
        h1,
        h2,
        h3,
        h4,
        h5,
        h6,
        p,
        span,
        strong,
        a,
        li,
        ui {
          opacity: 0.4;
        }
        :after {
          background: ${getRgbaValue(themeColors.states.invertAlpha, 0.4)};
        }
      `
      const borderColor =
        bss === 'disabled'
          ? themeColors.stroke.tertiary
          : bss === 'focused'
            ? themeColors.stroke.primary
            : themeColors.stroke.secondary

      rules = css`
        position: relative;
        background: transparent;
        overflow: hidden;
        border: 1px solid ${borderColor};
        :after {
          content: '';
          width: 100%;
          height: 100%;
          background: ${getRgbaValue(
        themeColors.states[alphaColorKey],
        alphaPercentage,
      )};
          transition: background 0.3s ease-in-out;
          position: absolute;
        }
        ${nh
          ? ''
          : css`
              :hover {
                :after {
                  background: ${getRgbaValue(
            themeColors.states.minimalAlpha,
            0.08,
          )};
                }
              }
            `}
        ${bss === 'disabled' && disabledState}
        :disabled {
          ${disabledState}
        }
      `
    }

    return css`
      ${coreRules}${rules}
    `
  }}
`

export const overlayPageCss = css`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    margin-left: auto;
    margin-right: auto;
    max-width: calc(420px + 208px);
  }
`
